import {httpsCallable} from "firebase/functions";
import {auth, functions} from "../firebaseConfig";

export const PHONE_STARTUP_TIMEOUT_MS = 15000;
const HANDOFF_MS = 5000;
const PREFIX = "lightly:journey:";
// staff_account: a church owner/admin account with no person record; not prompted to register.
const STATES = new Set(["linked", "already_linked", "verify_phone", "no_match", "review_required", "staff_account"]);
let journeys = new WeakMap(), checked = new WeakMap(), settingsPending, sessionGeneration = 0;

function deadline(operation) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      settled = true;
      reject(Object.assign(new Error("The phone sign-in check timed out. Please retry."), {code: "deadline-exceeded"}));
    }, PHONE_STARTUP_TIMEOUT_MS);
    Promise.resolve().then(operation).then(value => {
      if (!settled) {settled = true;clearTimeout(timer);resolve(value);}
    }, error => {
      if (!settled) {settled = true;clearTimeout(timer);reject(error);}
    });
  });
}

function key(user, orgId) {
  return PREFIX + JSON.stringify([user.uid, orgId, user.emailVerified === true, user.phoneNumber || "", user.metadata?.lastSignInTime || ""]);
}

function sessionIsCurrent(user, orgId, expectedKey) {
  return Boolean(user && auth.currentUser === user && key(user, orgId) === expectedKey);
}

function collection(store, user) {
  if (!store.has(user)) store.set(user, new Map());
  return store.get(user);
}

// These hints suppress repeat prompts only. They never establish identity,
// membership or access; every protected action still uses its server guards.
export function journeyWasChecked(user, orgId) {
  const id = key(user, orgId);
  if (checked.get(user)?.has(id)) return true;
  try {return sessionStorage.getItem(id) === "checked";} catch {return false;}
}

export function markJourneyChecked(user, orgId) {
  if (auth.currentUser !== user) return;
  const id = key(user, orgId);
  collection(checked, user).set(id, true);
  try {sessionStorage.setItem(id, "checked");} catch { /* memory-only prompt hint remains available */ }
}

export function clearJourneySessionHints() {
  sessionGeneration++;
  journeys = new WeakMap();checked = new WeakMap();
  try {
    const owned = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const id = sessionStorage.key(i);
      if (id?.startsWith(PREFIX) || id?.startsWith("lightly:identity-reconciled:")) owned.push(id);
    }
    owned.forEach(id => sessionStorage.removeItem(id));
  } catch { /* private browsing must not break sign-in */ }
}

export function invalidateJourneyStatus(user, orgId) {
  if (!user) return;
  const id = key(user, orgId);
  journeys.get(user)?.delete(id);checked.get(user)?.delete(id);
  try {sessionStorage.removeItem(id);} catch { /* hints are not security state */ }
}

export function requestJourneyStatus(user, orgId, {fresh = false} = {}) {
  const id = key(user, orgId);
  if (!sessionIsCurrent(user, orgId, id)) return Promise.reject(new Error("Your account changed. Please retry."));
  const entries = collection(journeys, user), existing = entries.get(id);
  if (!fresh && existing && (!existing.expires || existing.expires > Date.now())) return existing.promise;
  const entry = {expires: 0}, generation = sessionGeneration;
  entries.set(id, entry);
  entry.promise = deadline(() => {
    if (generation !== sessionGeneration || !sessionIsCurrent(user, orgId, id) || entries.get(id) !== entry) throw new Error("Your profile check changed. Please retry.");
    return httpsCallable(functions, "completePhoneRegistration")({orgId, action: "journey_status"});
  }).then(response => {
    if (generation !== sessionGeneration || !sessionIsCurrent(user, orgId, id) || entries.get(id) !== entry) throw new Error("Your profile check changed. Please retry.");
    if (!STATES.has(response?.data?.state)) throw new Error("We could not check your profile. Please retry.");
    entry.expires = Date.now() + HANDOFF_MS;
    return response;
  }).catch(error => {
    if (entries.get(id) === entry) entries.delete(id);
    throw error;
  });
  return entry.promise;
}

// Public settings share only an in-flight request, never a persisted/stale result.
export function requestPhoneSettings() {
  if (settingsPending) return settingsPending;
  const pending = deadline(() => httpsCallable(functions, "getPhoneRegistrationSettings")({}));
  settingsPending = pending;
  pending.then(() => {if (settingsPending === pending) settingsPending = null;}, () => {if (settingsPending === pending) settingsPending = null;});
  return pending;
}
